<!-- frontend/src/views/ActivityApproval.vue -->
<template>
  <div class="activity-approval">
    <h2>活動申請審核</h2>
    
    <!-- 權限檢查 -->
    <div v-if="!hasPermission" class="no-permission">
      <p>您沒有權限查看此頁面</p>
      <button @click="$router.push('/')">返回首頁</button>
    </div>
    
    <div v-else>
      <!-- 主分頁：活動申請審核 / 活動刪除審核（刪除審核僅 admin）-->
      <div class="main-tabs">
        <button :class="['main-tab-btn', { active: mainTab === 'apply' }]" @click="mainTab = 'apply'">
          活動申請審核
        </button>
        <button v-if="isAdmin" :class="['main-tab-btn', { active: mainTab === 'delete' }]" @click="switchToDelete">
          活動刪除審核
          <span class="count">({{ deletePendingCount }})</span>
        </button>
      </div>

      <!-- ===== 活動申請審核 ===== -->
      <div v-show="mainTab === 'apply'">
      <!-- 篩選標籤 -->
      <div class="filter-tabs">
        <button 
          v-for="status in statusOptions" 
          :key="status.value"
          @click="currentStatus = status.value"
          :class="['tab-btn', { active: currentStatus === status.value }]"
        >
          {{ status.label }}
          <span class="count">({{ getCountByStatus(status.value) }})</span>
        </button>
      </div>
      
      <!-- 申請列表 -->
      <div v-if="loading" class="loading">
        載入中...
      </div>
      
      <div v-else-if="filteredApplications.length === 0" class="no-data">
        沒有{{ currentStatusLabel }}的申請
      </div>
      
      <div v-else class="applications-list">
        <div 
          v-for="app in filteredApplications" 
          :key="app.id"
          class="application-card"
          :class="app.status"
        >
          <!-- 申請資訊 -->
          <div class="app-header">
            <h3>{{ app.title }}</h3>
            <span class="status-badge" :class="app.status">
              {{ getStatusLabel(app.status) }}
            </span>
          </div>
          
          <div class="app-info">
            <div class="info-row">
              <span class="label">申請人：</span>
              <span>{{ app.applicant_name || `用戶 ${app.applicant_id}` }}</span>
            </div>
            <div class="info-row">
              <span class="label">申請時間：</span>
              <span>{{ formatDateTime(app.created_at) }}</span>
            </div>
            <div class="info-row">
              <span class="label">活動日期：</span>
              <span>{{ formatDateTime(app.start_date) }} ~ {{ formatDateTime(app.end_date) }}</span>
            </div>
            <div class="info-row">
              <span class="label">地點：</span>
              <span>{{ app.location }}</span>
            </div>
            <div class="info-row">
              <span class="label">講者：</span>
              <span>{{ app.speaker || '無' }}</span>
            </div>
            <div class="info-row">
              <span class="label">學習時數：</span>
              <span>{{ app.learning_hours }} 小時</span>
            </div>
            <div class="info-row">
              <span class="label">類別：</span>
              <span>{{ categoryLabel(app.category) }}</span>
            </div>
            <div class="info-row">
              <span class="label">報名截止：</span>
              <span>{{ app.reg_deadline ? formatDateTime(app.reg_deadline) : '以活動開始時間為截止' }}</span>
            </div>
            <div class="info-row">
              <span class="label">人數上限：</span>
              <span>{{ app.max_participants ? app.max_participants + ' 人' : '不限' }}</span>
            </div>
            <div class="description">
              <span class="label">活動說明：</span>
              <p>{{ stripHtml(app.description) }}</p>
            </div>
          </div>
          
          <!-- 審核資訊（已審核的顯示） -->
          <div v-if="app.status !== 'pending'" class="review-info">
            <div class="info-row">
              <span class="label">審核人：</span>
              <span>{{ app.reviewer_name || `審核者 ${app.reviewer_id}` }}</span>
            </div>
            <div class="info-row">
              <span class="label">審核時間：</span>
              <span>{{ formatDateTime(app.review_date) }}</span>
            </div>
            <div v-if="app.review_comment" class="info-row">
              <span class="label">審核意見：</span>
              <span>{{ app.review_comment }}</span>
            </div>
          </div>
          
          <!-- 審核操作（待審核的顯示） -->
          <div v-if="app.status === 'pending'" class="review-actions">
            <div class="comment-input">
              <label>審核意見：</label>
              <textarea 
                v-model="reviewComments[app.id]" 
                placeholder="請輸入審核意見（選填）"
                rows="3"
              ></textarea>
            </div>
            <div class="action-buttons">
              <button 
                @click="approveApplication(app)"
                class="approve-btn"
                :disabled="processing[app.id]"
              >
                {{ processing[app.id] ? '處理中...' : '通過' }}
              </button>
              <button 
                @click="rejectApplication(app)"
                class="reject-btn"
                :disabled="processing[app.id]"
              >
                {{ processing[app.id] ? '處理中...' : '駁回' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
      <!-- ===== 活動刪除審核（僅 admin）===== -->
      <div v-show="mainTab === 'delete'">
        <div v-if="deleteLoading" class="loading">載入中...</div>
        <div v-else-if="deleteRequests.length === 0" class="no-data">目前沒有活動刪除申請</div>
        <div v-else class="applications-list">
          <div v-for="req in deleteRequests" :key="'d' + req.id" class="application-card" :class="req.status">
            <div class="app-header">
              <h3>{{ req.activity_title }}</h3>
              <span class="status-badge" :class="req.status">{{ getStatusLabel(req.status) }}</span>
            </div>
            <div class="app-info">
              <div class="info-row"><span class="label">申請人：</span><span>{{ req.requester_name || req.requester_id }}</span></div>
              <div class="info-row"><span class="label">申請時間：</span><span>{{ formatDateTime(req.created_at) }}</span></div>
              <div class="info-row"><span class="label">活動編號：</span><span>{{ req.activity_id }}</span></div>
              <div class="description"><span class="label">刪除原因：</span><p>{{ req.reason }}</p></div>
            </div>
            <div v-if="req.status !== 'pending'" class="review-info">
              <div class="info-row"><span class="label">審核人：</span><span>{{ req.reviewer_name || req.reviewer_id }}</span></div>
              <div class="info-row"><span class="label">審核時間：</span><span>{{ formatDateTime(req.review_at) }}</span></div>
              <div v-if="req.review_comment" class="info-row"><span class="label">審核意見：</span><span>{{ req.review_comment }}</span></div>
            </div>
            <div v-if="req.status === 'pending'" class="review-actions">
              <div class="comment-input">
                <label>審核意見：</label>
                <textarea v-model="deleteComments[req.id]" placeholder="請輸入審核意見（選填）" rows="3"></textarea>
              </div>
              <div class="action-buttons">
                <button @click="approveDelete(req)" class="approve-btn" :disabled="deleteProcessing[req.id]">
                  {{ deleteProcessing[req.id] ? '處理中...' : '核准刪除' }}
                </button>
                <button @click="rejectDelete(req)" class="reject-btn" :disabled="deleteProcessing[req.id]">
                  {{ deleteProcessing[req.id] ? '處理中...' : '退回' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getActivityApplications, reviewApplication, getActivityDeleteRequests, reviewActivityDeleteRequest } from '@/api.js'
import { categoryLabel as catLabel } from '@/utils/category.js'

export default {
  name: 'ActivityApproval',
  data() {
    return {
      hasPermission: false,
      applications: [],
      loading: false,
      currentStatus: 'pending',
      statusOptions: [
        { value: 'pending', label: '待審核' },
        { value: 'approved', label: '已通過' },
        { value: 'rejected', label: '已駁回' },
        { value: 'all', label: '全部' }
      ],
      reviewComments: {},
      processing: {}, // Vue 3 中直接使用響應式物件
      userRole: '',
      // 主分頁 + 活動刪除審核
      mainTab: 'apply',
      deleteRequests: [],
      deleteLoading: false,
      deleteComments: {},
      deleteProcessing: {}
    }
  },
  computed: {
    isAdmin() {
      return this.userRole === 'admin'
    },
    deletePendingCount() {
      return this.deleteRequests.filter(r => r.status === 'pending').length
    },
    filteredApplications() {
      if (this.currentStatus === 'all') {
        return this.applications
      }
      return this.applications.filter(app => app.status === this.currentStatus)
    },
    currentStatusLabel() {
      const option = this.statusOptions.find(opt => opt.value === this.currentStatus)
      return option ? option.label : ''
    }
  },
  async created() {
    this.checkPermission()
    if (this.hasPermission) {
      await this.loadApplications()
      if (this.isAdmin) await this.loadDeleteRequests()
    }
  },
  methods: {
    // 去除活動簡述的 HTML 標籤，審核列表只顯示純文字摘要
    stripHtml(html) {
      if (!html) return ''
      try {
        const doc = new DOMParser().parseFromString(String(html), 'text/html')
        return (doc.body.textContent || '').trim()
      } catch (e) {
        return String(html).replace(/<[^>]*>/g, '')
      }
    },
    checkPermission() {
      const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}')
      this.userRole = userInfo.role || ''
      this.hasPermission = ['dept', 'admin'].includes(this.userRole)
      console.log('權限檢查 - 使用者角色:', this.userRole, '是否有權限:', this.hasPermission)
    },
    
    async loadApplications() {
      this.loading = true
      try {
        console.log('載入活動申請列表...')
        const response = await getActivityApplications()
        this.applications = response.data
        console.log('載入申請列表成功:', this.applications.length, '筆')
      } catch (error) {
        console.error('載入申請列表失敗:', error)
        this.applications = []
        
        // 如果是權限錯誤，顯示具體訊息
        if (error.response?.status === 403) {
          alert('權限不足，無法查看申請列表')
          this.hasPermission = false
        }
      } finally {
        this.loading = false
      }
    },
    
    getCountByStatus(status) {
      if (status === 'all') {
        return this.applications.length
      }
      return this.applications.filter(app => app.status === status).length
    },
    
    getStatusLabel(status) {
      const statusMap = {
        'pending': '待審核',
        'approved': '已通過',
        'rejected': '已駁回'
      }
      return statusMap[status] || status
    },
    
    async approveApplication(app) {
      if (!confirm(`確定要通過「${app.title}」的申請嗎？`)) {
        return
      }
      
      // Vue 3 中直接賦值即可
      this.processing[app.id] = true
      console.log(`開始審核通過申請 ${app.id}`)
      
      try {
        await reviewApplication(app.id, {
          status: 'approved',
          comment: this.reviewComments[app.id] || ''
        })
        
        alert('審核通過')
        await this.loadApplications()
        console.log(`申請 ${app.id} 審核通過`)
      } catch (error) {
        console.error('審核失敗:', error)
        alert('審核失敗：' + (error.response?.data?.detail || '系統錯誤'))
      } finally {
        this.processing[app.id] = false
      }
    },
    
    async rejectApplication(app) {
      const comment = this.reviewComments[app.id]
      
      // 修改：通過和駁回都可以選填審核意見，但駁回建議填寫
      if (!comment || comment.trim() === '') {
        if (!confirm(`確定要駁回「${app.title}」的申請嗎？\n建議填寫駁回原因以便申請人了解。`)) {
          return
        }
      } else {
        if (!confirm(`確定要駁回「${app.title}」的申請嗎？`)) {
          return
        }
      }
      
      // Vue 3 中直接賦值即可
      this.processing[app.id] = true
      console.log(`開始審核駁回申請 ${app.id}`)
      
      try {
        await reviewApplication(app.id, {
          status: 'rejected',
          comment: comment || '無具體說明'
        })
        
        alert('已駁回申請')
        await this.loadApplications()
        console.log(`申請 ${app.id} 已駁回`)
      } catch (error) {
        console.error('審核失敗:', error)
        alert('審核失敗：' + (error.response?.data?.detail || '系統錯誤'))
      } finally {
        this.processing[app.id] = false
      }
    },
    
    // ===== 活動刪除審核 =====
    switchToDelete() {
      this.mainTab = 'delete'
      this.loadDeleteRequests()
    },
    async loadDeleteRequests() {
      this.deleteLoading = true
      try {
        const res = await getActivityDeleteRequests()
        this.deleteRequests = res.data
      } catch (error) {
        console.error('載入刪除申請失敗:', error)
        this.deleteRequests = []
        if (error.response?.status === 403) alert('權限不足，無法查看刪除申請')
      } finally {
        this.deleteLoading = false
      }
    },
    async approveDelete(req) {
      if (!confirm(`確定要核准刪除「${req.activity_title}」嗎？\n核准後活動與報名資料將被永久刪除，無法復原。`)) return
      this.deleteProcessing[req.id] = true
      try {
        await reviewActivityDeleteRequest(req.id, 'approved', this.deleteComments[req.id] || '')
        alert('已核准並刪除活動')
        await this.loadDeleteRequests()
      } catch (error) {
        alert('審核失敗：' + (error.response?.data?.detail || '系統錯誤'))
      } finally {
        this.deleteProcessing[req.id] = false
      }
    },
    async rejectDelete(req) {
      const comment = this.deleteComments[req.id]
      if (!confirm(`確定要退回「${req.activity_title}」的刪除申請嗎？`)) return
      this.deleteProcessing[req.id] = true
      try {
        await reviewActivityDeleteRequest(req.id, 'rejected', comment || '無具體說明')
        alert('已退回刪除申請')
        await this.loadDeleteRequests()
      } catch (error) {
        alert('審核失敗：' + (error.response?.data?.detail || '系統錯誤'))
      } finally {
        this.deleteProcessing[req.id] = false
      }
    },

    categoryLabel(category) {
      return catLabel(category, '未分類')
    },

    formatDateTime(dateString) {
      if (!dateString) return ''

      try {
        const date = new Date(dateString)
        if (isNaN(date.getTime())) return '日期格式錯誤'
        
        return date.toLocaleString('zh-TW', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
      } catch (error) {
        console.error('日期格式化錯誤:', error)
        return '日期解析失敗'
      }
    }
  }
}
</script>

<style scoped>
.activity-approval {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

h2 {
  color: #333;
  margin-bottom: 20px;
}

/* 權限提示 */
.no-permission {
  text-align: center;
  padding: 50px;
}

.no-permission button {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: #42b983;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

/* 篩選標籤 */
/* 主分頁（申請審核 / 刪除審核） */
.main-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}
.main-tab-btn {
  padding: 10px 22px;
  background: #f0f0f0;
  border: none;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  color: #555;
}
.main-tab-btn.active {
  background: #42b983;
  color: #fff;
}

.filter-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  border-bottom: 2px solid #e0e0e0;
  padding-bottom: 10px;
}

.tab-btn {
  padding: 8px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  transition: all 0.3s;
}

.tab-btn:hover {
  color: #42b983;
}

.tab-btn.active {
  color: #42b983;
  font-weight: bold;
  border-bottom: 3px solid #42b983;
}

.count {
  font-size: 14px;
  color: #999;
}

/* 申請列表 */
.loading, .no-data {
  text-align: center;
  padding: 40px;
  color: #666;
}

.applications-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.application-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.application-card.approved {
  border-left: 4px solid #28a745;
}

.application-card.rejected {
  border-left: 4px solid #dc3545;
}

.application-card.pending {
  border-left: 4px solid #ffc107;
}

/* 申請資訊 */
.app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.app-header h3 {
  margin: 0;
  color: #333;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
}

.status-badge.pending {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge.approved {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.rejected {
  background-color: #f8d7da;
  color: #721c24;
}

.app-info {
  margin-bottom: 15px;
}

.info-row {
  display: flex;
  margin-bottom: 8px;
}

.label {
  font-weight: bold;
  color: #666;
  min-width: 100px;
}

.description {
  margin-top: 10px;
}

.description p {
  margin: 5px 0 0 0;
  color: #666;
  line-height: 1.5;
}

/* 審核資訊 */
.review-info {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 4px;
  margin-top: 15px;
}

/* 審核操作 */
.review-actions {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.comment-input {
  margin-bottom: 15px;
}

.comment-input label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #666;
}

.comment-input textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  resize: vertical;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.approve-btn, .reject-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s;
}

.approve-btn {
  background-color: #28a745;
  color: white;
}

.approve-btn:hover:not(:disabled) {
  background-color: #218838;
}

.reject-btn {
  background-color: #dc3545;
  color: white;
}

.reject-btn:hover:not(:disabled) {
  background-color: #c82333;
}

.approve-btn:disabled,
.reject-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 響應式設計 */
@media (max-width: 768px) {
  .filter-tabs {
    flex-wrap: wrap;
  }
  
  .app-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .info-row {
    flex-direction: column;
  }
  
  .label {
    min-width: auto;
    margin-bottom: 2px;
  }
  
  .action-buttons {
    flex-direction: column;
  }
  
  .approve-btn,
  .reject-btn {
    width: 100%;
  }
}
</style>